/**
 * Page /ticker — Bandelette défilante de la borne (serveur, tuile /manage).
 * @author Dr Hamid MADANI <drmdh@msn.com>
 *
 * Une ligne de message qui défile sur `/distributor`. Persistée dans `config.distributor.ticker`
 * (PUT /api/config, via `useConfig`) — donc la MÊME donnée que celle réglée depuis la console
 * centrale et depuis la configuration de la borne. Trois portes, un seul magasin : un message
 * lu par le public ne se saisit pas poste par poste, sous peine de dire deux choses
 * différentes dans la même salle.
 *
 * L'APERÇU EST LE COMPOSANT LUI-MÊME, pas une imitation. Le sens de défilement et la durée
 * d'une passe ne se jugent pas sur une description : il faut les voir. Et un aperçu
 * reconstitué à la main finirait par diverger de ce que la borne affiche vraiment —
 * l'exploitant validerait alors quelque chose qui n'existe pas.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfig } from '../../contexts/ConfigContext';
import BandeauDefilant from '../../components/BandeauDefilant';

const LANGUES = [
  { cle: 'ar', libelle: 'Arabe', dir: 'rtl', exemple: 'يرجى الاحتفاظ بتذكرتكم' },
  { cle: 'fr', libelle: 'Français', dir: 'ltr', exemple: 'Merci de conserver votre ticket' },
  { cle: 'en', libelle: 'English', dir: 'ltr', exemple: 'Please keep your ticket' },
];

function DistributorTicker() {
  const navigate = useNavigate();
  const { config, updateConfig, loading } = useConfig();

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState({ ar: '', fr: '', en: '' });
  const [direction, setDirection] = useState('rtl');
  const [duration, setDuration] = useState(20);
  /**
   * L'apparence tient dans UN objet, et non dans six `useState`.
   *
   * Ces réglages voyagent toujours ensemble — vers l'aperçu, vers la sauvegarde — et les
   * séparer obligerait à les recomposer à chaque fois, avec le risque d'en oublier un en
   * route : c'est exactement ainsi qu'un réglage cesse silencieusement d'avoir un effet.
   */
  const [apparence, setApparence] = useState({
    position: 'top', fontFamily: '', fontSize: 1.5, bold: true,
    color: '#f9fafb', backgroundColor: '#1f2937', separatorColor: '#60a5fa',
  });
  const majApparence = (champ, valeur) => setApparence((a) => ({ ...a, [champ]: valeur }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    const t = config?.distributor?.ticker || {};
    setEnabled(t.enabled === true);
    setMessage({
      ar: String(t.message?.ar ?? ''),
      fr: String(t.message?.fr ?? ''),
      en: String(t.message?.en ?? ''),
    });
    setDirection(t.direction === 'ltr' ? 'ltr' : 'rtl');
    setDuration(Number(t.duration) > 0 ? Number(t.duration) : 20);
    setApparence({
      position: t.position === 'bottom' ? 'bottom' : 'top',
      fontFamily: String(t.fontFamily ?? ''),
      fontSize: Number(t.fontSize) > 0 ? Number(t.fontSize) : 1.5,
      bold: t.bold !== false,
      color: t.color || '#f9fafb',
      backgroundColor: t.backgroundColor || '#1f2937',
      separatorColor: t.separatorColor || '#60a5fa',
    });
  }, [loading, config]);

  /**
   * Configuration de l'APERÇU : la vraie, avec la saisie en cours par-dessus.
   *
   * On repart de `config` et non d'un objet nu, pour que l'aperçu hérite de l'ORDRE et de la
   * VISIBILITÉ des langues réglés ailleurs (`styles.display`). Sans cela il montrerait trois
   * langues là où la borne n'en affichera qu'une, et l'exploitant validerait un rendu que
   * personne ne verra jamais.
   *
   * `enabled` est forcé : on veut voir ce qu'on écrit même avant d'avoir activé la bandelette.
   */
  const apercu = useMemo(() => ({
    ...(config || {}),
    distributor: {
      ...(config?.distributor || {}),
      ticker: { ...apparence, enabled: true, message, direction, duration },
    },
  }), [config, message, direction, duration, apparence]);

  const rienASaisi = !LANGUES.some((l) => String(message[l.cle] || '').trim());

  const save = async () => {
    setSaving(true);
    // Fusion NON destructive : on n'écrase que la branche `distributor.ticker`.
    const merged = JSON.parse(JSON.stringify(config || {}));
    merged.distributor = {
      ...(merged.distributor || {}),
      ticker: { ...apparence, enabled, message, direction, duration },
    };
    const ok = await updateConfig(merged);
    setSaving(false);
    if (ok) toast.success('Bandelette enregistrée');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6 md:p-10">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📢 Bandelette de la borne</h1>
            <p className="text-sm text-gray-500">
              Une ligne de message qui défile sur l'écran du <strong>distributeur</strong>.
              Le même réglage s'applique à toutes les bornes du site.
            </p>
          </div>
          <button onClick={() => navigate('/manage')} className="text-sm text-gray-600 hover:text-gray-900 underline">← Retour</button>
        </div>

        {/* L'aperçu EN PREMIER : c'est ce qu'on vient régler, il doit être sous les yeux
            pendant la saisie et non au bas d'un formulaire qu'il faut faire défiler. */}
        <div className="mb-6">
          <div className="text-sm font-medium text-gray-700 mb-2">Aperçu</div>
          {rienASaisi
            ? <div className="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-400 text-center">
                Saisissez un message pour voir la bandelette défiler.
              </div>
            : <div className="rounded-lg overflow-hidden shadow"><BandeauDefilant config={apercu} /></div>}
        </div>

        <div className="bg-white rounded-xl shadow p-6 border border-gray-100 space-y-6">
          <label className="flex items-center gap-3">
            <input type="checkbox" className="h-4 w-4" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            <span className="text-sm font-medium text-gray-700">Afficher la bandelette sur la borne</span>
          </label>

          <div className="space-y-3">
            <div className="block text-sm font-medium text-gray-700">Message</div>
            {/* Une saisie par langue, dans l'ordre et la direction d'écriture qui lui reviennent.
                Une langue laissée VIDE est simplement sautée sur la borne — ce n'est pas une
                erreur, c'est le moyen de n'afficher qu'une langue sans toucher aux réglages
                de langues du site. */}
            {LANGUES.map((l) => (
              <div key={l.cle}>
                <label className="block text-xs text-gray-500 mb-1">{l.libelle}</label>
                <input
                  type="text"
                  dir={l.dir}
                  value={message[l.cle]}
                  placeholder={l.exemple}
                  onChange={(e) => setMessage({ ...message, [l.cle]: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
            ))}
            <p className="text-xs text-gray-500">
              Les langues affichées et leur ordre suivent ceux des noms de service
              (console → <em>Styles → Affichage</em>). Une langue vide est ignorée.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sens de défilement</label>
              <select
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="rtl">De droite à gauche</option>
                <option value="ltr">De gauche à droite</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durée d'une passe (s)</label>
              <input
                type="number" min="5" max="180"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Plus grand = plus lent.</p>
            </div>
          </div>

          {/* ─── Apparence ──────────────────────────────────────────────────────
              Séparée du défilement à dessein : ce qui suit décrit à QUOI la bandelette
              ressemble, ce qui précède décrit comment elle bouge. Les deux se règlent à des
              moments différents — l'apparence une fois pour toutes, le message souvent. */}
          <div className="border-t border-gray-100 pt-6 space-y-4">
            <div className="text-sm font-medium text-gray-700">Apparence</div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Position sur la borne</label>
                <select
                  value={apparence.position}
                  onChange={(e) => majApparence('position', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="top">En haut, sous l'en-tête</option>
                  <option value="bottom">En bas, au-dessus de la publicité</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Police</label>
                <input
                  type="text"
                  value={apparence.fontFamily}
                  placeholder="(celle de la borne)"
                  onChange={(e) => majApparence('fontFamily', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Vide = la police du site.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Taille (rem)</label>
                <input
                  type="number" min="1" max="4" step="0.1"
                  value={apparence.fontSize}
                  onChange={(e) => majApparence('fontSize', Number(e.target.value))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>
              <label className="flex items-center gap-2 pb-2">
                <input
                  type="checkbox" className="h-4 w-4"
                  checked={apparence.bold}
                  onChange={(e) => majApparence('bold', e.target.checked)}
                />
                <span className="text-sm text-gray-700">Gras</span>
              </label>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { champ: 'color', libelle: 'Texte' },
                { champ: 'backgroundColor', libelle: 'Fond' },
                { champ: 'separatorColor', libelle: 'Séparateur' },
              ].map((c) => (
                <div key={c.champ}>
                  <label className="block text-sm text-gray-700 mb-1">{c.libelle}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={apparence[c.champ]}
                      onChange={(e) => majApparence(c.champ, e.target.value)}
                      className="h-9 w-12 rounded border border-gray-300 p-0.5"
                    />
                    {/* La valeur en clair À CÔTÉ du sélecteur : un exploitant qui doit
                        reproduire une charte graphique a une référence exacte à saisir,
                        là où un carré de couleur ne se recopie pas. */}
                    <input
                      type="text"
                      value={apparence[c.champ]}
                      onChange={(e) => majApparence(c.champ, e.target.value)}
                      className="w-full rounded-md border border-gray-300 px-2 py-2 text-xs font-mono"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2">
            <button
              onClick={save}
              disabled={saving || loading}
              className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DistributorTicker;
